import { useCallback, useEffect, useRef, useState } from 'react';
import { useCRMAuth, apiError } from '../../crm/context/CRMAuthContext';
import { managerAPI } from '../parity/useManagerData';
import { watchLiveRefresh } from '../../hooks/liveRefresh';

export const useMarketWatch = () => {
  const { user } = useCRMAuth();
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [online, setOnline] = useState(navigator.onLine);
  const [clock, setClock] = useState(Date.now());
  const epoch = useRef(null);
  const pending = useRef(null);
  const receivedAt = useRef(0);
  const identity = `${user?.id}:${user?.tenant_id}`;
  useEffect(() => {
    const timer = window.setTimeout(() => { setQuery(search.trim()); setPage(1); }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);
  const reload = useCallback(async () => {
    if (pending.current) return;
    const current = epoch.current;
    const controller = new AbortController();
    pending.current = controller;
    setClock(Date.now());
    setLoading(true);
    try {
      const response = await managerAPI.get('/market-watch', { params: { search: query, page, limit: 20 }, signal: controller.signal, timeout: 10000 });
      if (epoch.current !== current) return;
      receivedAt.current = Date.now();
      setData(response.data); setError(''); setOnline(navigator.onLine); setClock(receivedAt.current);
    } catch (failure) {
      if (epoch.current === current && !controller.signal.aborted) setError(apiError(failure));
    } finally {
      if (pending.current === controller) pending.current = null;
      if (epoch.current === current) setLoading(false);
    }
  }, [query, page, identity]);
  useEffect(() => {
    epoch.current = {};
    setData(null); setError('');
    reload();
    const stop = watchLiveRefresh(reload, 3000);
    const offline = () => { setOnline(false); setClock(Date.now()); };
    window.addEventListener('offline', offline);
    return () => { epoch.current = null; pending.current?.abort(); pending.current = null; stop(); window.removeEventListener('offline', offline); };
  }, [reload]);
  const stale = Boolean(error) || !online || (data && clock - receivedAt.current > 12000);
  return { data, search, setSearch, page, setPage, loading, error, online, stale, reload };
};